import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Clock, XCircle, Upload, FileText } from "lucide-react";
import { format } from "date-fns";

interface FulfillmentTrackerProps {
  dealId: string;
}

export const FulfillmentTracker = ({ dealId }: FulfillmentTrackerProps) => {
  const queryClient = useQueryClient();
  const [selectedDeliverable, setSelectedDeliverable] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: deliverables, isLoading } = useQuery({
    queryKey: ["deliverables", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deliverables")
        .select("*")
        .eq("deal_id", dealId)
        .order("due_date");
      
      if (error) throw error;
      return data;
    },
  });

  const markCompletedMutation = useMutation({
    mutationFn: async ({ id, proof_url, notes }: { id: string; proof_url?: string; notes?: string }) => {
      const { error } = await supabase
        .from("deliverables")
        .update({
          status: "completed",
          submitted_at: new Date().toISOString(),
          proof_url: proof_url || undefined,
          notes: notes || undefined,
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliverables", dealId] });
      toast.success("Deliverable marked as completed");
      setSelectedDeliverable(null);
      setProofUrl("");
      setNotes("");
    },
    onError: () => {
      toast.error("Failed to update deliverable");
    },
  });

  const uploadProof = async (file: File) => {
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${dealId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('deal-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('deal-documents')
        .getPublicUrl(fileName);

      setProofUrl(publicUrl);
      toast.success("Proof uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload proof");
    } finally {
      setUploading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">Completed</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  if (isLoading) {
    return <div>Loading deliverables...</div>;
  }

  const pendingCount = deliverables?.filter(d => d.status === "pending").length || 0;
  const completedCount = deliverables?.filter(d => d.status === "completed").length || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deal Fulfillment Tracker</CardTitle>
        <CardDescription>
          Track and manage deliverables for this deal. {completedCount}/{deliverables?.length || 0} completed
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {deliverables?.length === 0 ? (
          <p className="text-muted-foreground">No deliverables for this deal yet.</p>
        ) : (
          deliverables?.map((deliverable) => (
            <div key={deliverable.id} className="flex items-start gap-4 p-4 border rounded-lg">
              <div className="mt-1">{getStatusIcon(deliverable.status)}</div>
              
              <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{deliverable.description}</h4>
                    <p className="text-sm text-muted-foreground">
                      Due: {format(new Date(deliverable.due_date), "MMM d, yyyy")}
                    </p>
                  </div>
                  {getStatusBadge(deliverable.status)}
                </div>

                {deliverable.proof_url && (
                  <a
                    href={deliverable.proof_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <FileText className="h-4 w-4" />
                    View Proof of Performance
                  </a>
                )}

                {deliverable.notes && (
                  <p className="text-sm text-muted-foreground">
                    <strong>Notes:</strong> {deliverable.notes}
                  </p>
                )}

                {deliverable.submitted_at && (
                  <p className="text-sm text-muted-foreground">
                    Submitted: {format(new Date(deliverable.submitted_at), "MMM d, yyyy h:mm a")}
                  </p>
                )}

                {deliverable.status === "pending" && (
                  <Dialog open={selectedDeliverable === deliverable.id} onOpenChange={(open) => !open && setSelectedDeliverable(null)}>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={() => setSelectedDeliverable(deliverable.id)}>
                        Mark as Completed
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Mark Deliverable as Completed</DialogTitle>
                        <DialogDescription>
                          Add proof of performance and any notes about this deliverable.
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Upload Proof (Optional)</Label>
                          <Input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadProof(file);
                            }}
                            disabled={uploading}
                          />
                          {proofUrl && (
                            <p className="text-sm text-green-600">✓ Proof uploaded</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Proof URL (Optional)</Label>
                          <Input
                            placeholder="https://instagram.com/p/..."
                            value={proofUrl}
                            onChange={(e) => setProofUrl(e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Notes (Optional)</Label>
                          <Textarea
                            placeholder="Add any notes about this deliverable..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                          />
                        </div>
                      </div>

                      <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedDeliverable(null)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={() => markCompletedMutation.mutate({
                            id: deliverable.id,
                            proof_url: proofUrl,
                            notes,
                          })}
                          disabled={markCompletedMutation.isPending}
                        >
                          Mark as Completed
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
